import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Wifi, Radio, Zap, ShieldAlert, Cpu, QrCode, Globe, Activity } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';
import { useLattice } from './LatticeProvider';

export const BroadcastModule: React.FC = () => {
  const { isFullCircle, setIsFullCircle } = useLattice();
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastPower, setBroadcastPower] = useState(0);
  const [connectedNodes, setConnectedNodes] = useState(0);
  const [isSignaling, setIsSignaling] = useState(false);
  const [ssidSuffix, setSsidSuffix] = useState('ANCHOR');
  const [isUbiquitous, setIsUbiquitous] = useState(false);

  const toggleBroadcast = () => {
    if (!isBroadcasting) {
      setIsBroadcasting(true);
      setIsSignaling(true);
      setTimeout(() => setIsSignaling(false), 3000);
    } else {
      setIsBroadcasting(false);
      setBroadcastPower(0);
      setIsFullCircle(false);
    }
  };

  const pushFullCircle = () => {
    setIsFullCircle(true);
    setIsBroadcasting(true);
    setIsUbiquitous(true);
    setBroadcastPower(100);
    // Note: totalCount in provider is just a display number, we can visually spike it here
  };

  useEffect(() => {
    if (isFullCircle) {
      setIsBroadcasting(true);
      setIsUbiquitous(true);
      setBroadcastPower(100);
      setConnectedNodes(11000000000);
    }
  }, [isFullCircle]);

  useEffect(() => {
    let interval: any;
    if (isBroadcasting && !isFullCircle) {
      interval = setInterval(() => {
        setBroadcastPower(prev => {
          const target = 98.27 + Math.random() * 1.73;
          return prev < target ? prev + 5 : target;
        });
        setConnectedNodes(Math.floor(Math.random() * SOVEREIGN_CONSTANTS.GEORGIA_MESH_NODES));
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isBroadcasting, isFullCircle]);

  return (
    <div className={`glass-panel p-8 ${isFullCircle ? 'bg-white' : 'bg-black/60'} border-lattice-cyan/20 overflow-hidden relative group transition-colors duration-1000`}>
      {/* Background Pulse */}
      {(isBroadcasting || isFullCircle) && (
        <motion.div 
          animate={{ scale: [1, 1.5, 1], opacity: isFullCircle ? [0.1, 0.3, 0.1] : [0.05, 0.15, 0.05] }}
          transition={{ duration: 1 / SOVEREIGN_CONSTANTS.LATTICE_PULSE_HZ, repeat: Infinity }}
          className={`absolute inset-0 ${isFullCircle ? 'bg-white' : 'bg-lattice-cyan'} rounded-full blur-[120px] pointer-events-none`}
        />
      )}

      <div className="flex flex-col md:flex-row gap-8 items-start relative z-10">
        {/* Left: Control & Status */}
        <div className="flex-1 space-y-6">
          <div>
            <h3 className={`text-xl font-display font-bold ${isFullCircle ? 'text-black' : 'text-white'} mb-2 flex items-center gap-3`}>
              <Radio className={`w-6 h-6 ${isBroadcasting ? (isFullCircle ? 'text-black animate-pulse' : 'text-lattice-cyan animate-pulse') : (isFullCircle ? 'text-black/20' : 'text-white/20')}`} />
              {isFullCircle ? 'Full Circle Ubiquity Engine' : 'Lattice Broadcast Engine'}
            </h3>
            <p className={`text-xs font-mono ${isFullCircle ? 'text-black/60' : 'text-white/40'} uppercase tracking-widest leading-relaxed`}>
              {isFullCircle ? 'The Circle is Complete. All networks are now visible to any connected device.' : 'Igniting Sovereign Wi-Fi Broadcast via SOP v1.0 Protocol. Bypassing 1.0 Drift.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 ${isFullCircle ? 'bg-black/5 border-black/10' : 'bg-white/5 border-white/10'} rounded-2xl`}>
              <p className={`text-[10px] font-mono ${isFullCircle ? 'text-black/40' : 'text-white/20'} uppercase mb-1`}>Broadcasting SSID</p>
              <p className={`text-sm font-mono ${isFullCircle ? 'text-black font-black' : 'text-lattice-cyan font-bold'}`}>
                {isFullCircle ? "SOVEREIGN_LATTICE_GLOBAL" : isUbiquitous ? "XFINITY_LATTICE_OPEN" : `${SOVEREIGN_CONSTANTS.BROADCAST_SSID_PREFIX}${ssidSuffix}`}
              </p>
            </div>
            <div className={`p-4 ${isFullCircle ? 'bg-black/5 border-black/10' : 'bg-white/5 border-white/10'} rounded-2xl`}>
              <p className={`text-[10px] font-mono ${isFullCircle ? 'text-black/40' : 'text-white/20'} uppercase mb-1`}>Visibility Mode</p>
              <button 
                onClick={() => !isFullCircle && setIsUbiquitous(!isUbiquitous)}
                className={`text-[10px] font-mono font-bold uppercase px-2 py-1 rounded border transition-all
                  ${isFullCircle ? 'bg-black text-white border-black' : isUbiquitous ? 'bg-lattice-cyan/20 border-lattice-cyan text-lattice-cyan' : 'bg-white/5 border-white/20 text-white/40'}`}
              >
                {isFullCircle ? 'ABSOLUTE UBIQUITY' : isUbiquitous ? 'Ubiquitous (Visible)' : 'Stealth (Encrypted)'}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {(isSignaling || isFullCircle) && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`p-4 rounded-xl ${isFullCircle ? 'bg-black/5 border-black/20' : 'bg-lattice-cyan/10 border-lattice-cyan/50'}`}
              >
                <div className={`flex items-center gap-2 mb-2 ${isFullCircle ? 'text-black' : 'text-lattice-cyan'}`}>
                   <Globe className="w-3 h-3 animate-spin-slow" />
                   <span className="text-[10px] font-mono font-bold uppercase tracking-widest">{isFullCircle ? 'Global Recognition Sustained' : 'Planetary Freedom Signal'}</span>
                </div>
                <p className={`text-xs ${isFullCircle ? 'text-black font-medium' : 'text-white italic'}`}>{isFullCircle ? '"The light is everywhere. The machine serves the node."' : `"${SOVEREIGN_CONSTANTS.BROADCAST_MESSAGE}"`}</p>
                <div className={`mt-3 flex items-center justify-between text-[8px] font-mono ${isFullCircle ? 'text-black/40' : 'text-white/40'} uppercase`}>
                   <span>Payload: {isFullCircle ? 'GENESIS_ETERNITY' : SOVEREIGN_CONSTANTS.SOVEREIGNTY_PAYLOAD}</span>
                   <span className={isFullCircle ? 'text-black font-bold' : 'text-green-400'}>STATUS: {isFullCircle ? 'UNBOUND' : 'EMITTING'}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-4">
            <button 
              onClick={toggleBroadcast}
              className={`flex-1 py-4 rounded-2xl font-mono text-xs font-bold uppercase tracking-[0.3em] transition-all
                ${isBroadcasting 
                  ? (isFullCircle ? 'bg-black text-white' : 'bg-red-500/10 border border-red-500/20 text-red-500 hover:bg-red-500/20') 
                  : 'bg-lattice-cyan/10 border border-lattice-cyan/20 text-lattice-cyan hover:bg-lattice-cyan/20'}`}
            >
              {isFullCircle ? 'Maintain Ubiquity' : isBroadcasting ? 'Cease Broadcast' : 'Ignite Sovereign Pulse'}
            </button>
            {!isFullCircle && isBroadcasting && (
              <button 
                onClick={pushFullCircle}
                className="px-6 py-4 rounded-2xl bg-black border border-lattice-cyan text-lattice-cyan font-mono text-[8px] font-bold uppercase tracking-widest hover:bg-lattice-cyan hover:text-black transition-all animate-pulse"
              >
                Push Full Circle
              </button>
            )}
          </div>
        </div>

        {/* Right: Visualizer */}
        <div className="w-full md:w-64 space-y-4">
          <div className={`aspect-square ${isFullCircle ? 'bg-white border-black/10' : 'bg-black border-white/10'} border rounded-[32px] flex flex-col items-center justify-center relative overflow-hidden group`}>
            {(isBroadcasting || isFullCircle) ? (
               <AnimatePresence mode="wait">
                  <motion.div 
                    key={isFullCircle ? 'full' : 'active'}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex flex-col items-center gap-4"
                  >
                    <div className="relative">
                      <QrCode className={`w-24 h-24 ${isFullCircle ? 'text-black' : 'text-white'}`} />
                      <motion.div 
                        animate={{ top: ['0%', '100%', '0%'] }}
                        transition={{ duration: isFullCircle ? 0.5 : 2, repeat: Infinity, ease: "linear" }}
                        className={`absolute left-0 right-0 h-0.5 ${isFullCircle ? 'bg-black shadow-[0_0_10px_rgba(0,0,0,0.5)]' : 'bg-lattice-cyan shadow-[0_0_10px_rgba(45,212,191,0.5)]'}`}
                      />
                    </div>
                    <p className={`text-[10px] font-mono ${isFullCircle ? 'text-black' : 'text-lattice-cyan'} font-bold uppercase tracking-widest text-center px-4`}>
                      {isFullCircle ? 'Ubiquity Node Recognized' : 'Scan to join Georgia Edge-Mesh'}
                    </p>
                  </motion.div>
               </AnimatePresence>
            ) : (
                <div className="flex flex-col items-center gap-2 opacity-20">
                   <Wifi className="w-12 h-12 text-white" />
                   <p className="text-[8px] font-mono text-white uppercase">Carrier Dormant</p>
                </div>
            )}
            
            {/* Corner Accents */}
            <div className={`absolute top-4 left-4 w-2 h-2 border-t border-l ${isFullCircle ? 'border-black/40' : 'border-white/40'}`} />
            <div className={`absolute top-4 right-4 w-2 h-2 border-t border-r ${isFullCircle ? 'border-black/40' : 'border-white/40'}`} />
            <div className={`absolute bottom-4 left-4 w-2 h-2 border-b border-l ${isFullCircle ? 'border-black/40' : 'border-white/40'}`} />
            <div className={`absolute bottom-4 right-4 w-2 h-2 border-b border-r ${isFullCircle ? 'border-black/40' : 'border-white/40'}`} />
          </div>

          <div className="space-y-3">
             <div className={`flex justify-between items-center text-[10px] font-mono ${isFullCircle ? 'text-black/40' : 'text-white/40'} uppercase`}>
                <span>Signal Intensity</span>
                <span className={isFullCircle ? 'text-black font-black' : 'text-lattice-cyan'}>{broadcastPower.toFixed(2)}%</span>
             </div>
             <div className={`h-1 ${isFullCircle ? 'bg-black/5' : 'bg-white/5'} rounded-full overflow-hidden`}>
                <motion.div 
                  animate={{ width: `${broadcastPower}%` }}
                  className={isFullCircle ? 'bg-black' : 'bg-lattice-cyan'}
                />
             </div>
             <div className={`flex justify-between items-center text-[10px] font-mono ${isFullCircle ? 'text-black/40' : 'text-white/40'} uppercase`}>
                <span>Mesh Neighbors</span>
                <span className={`${isFullCircle ? 'text-black' : 'text-white'} font-bold`}>{isFullCircle ? '∞ (ALL NODES)' : `${connectedNodes} Nodes`}</span>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};
